import { MarketEmptyState } from '../components/MarketEmptyState'
import { StatusPill } from '../components/StatusPill'
import { standardPlanUiCopy, startPlanUiCopy } from '../billing/startPlanUiCopy'
import type { RfqDto, SupplierRfqInboxItemDto } from '../api/types'
import { rfqDisplayLabel } from '../api/rfqLabels'
import {
  formatDate,
  formatStatus,
  restaurantDisplayName,
  restaurantSupplierDecision,
  supplierDecisionStatus,
} from './ordersUtils'

type OrdersListPanelProps = {
  isSupplier: boolean
  isStartPlan: boolean
  actionFilter: boolean
  error: string | null
  archiveExpanded: boolean
  activeCount: number
  archivedCount: number
  totalCount: number
  activeSupplierOrders: SupplierRfqInboxItemDto[]
  archivedSupplierOrders: SupplierRfqInboxItemDto[]
  activeRestaurantOrders: RfqDto[]
  archivedRestaurantOrders: RfqDto[]
  selectedSupplierInboxId: number | null
  selectedRestaurantRfqId: number | null
  onSelectSupplierItem: (item: SupplierRfqInboxItemDto) => void
  onSelectRestaurantRfq: (rfq: RfqDto) => void
  onClearActionFilter: () => void
  onToggleArchive: () => void
}

export const OrdersListPanel = ({
  isSupplier,
  isStartPlan,
  actionFilter,
  error,
  archiveExpanded,
  activeCount,
  archivedCount,
  totalCount,
  activeSupplierOrders,
  archivedSupplierOrders,
  activeRestaurantOrders,
  archivedRestaurantOrders,
  selectedSupplierInboxId,
  selectedRestaurantRfqId,
  onSelectSupplierItem,
  onSelectRestaurantRfq,
  onClearActionFilter,
  onToggleArchive,
}: OrdersListPanelProps) => {
  const uiCopy = isStartPlan ? startPlanUiCopy : standardPlanUiCopy

  const renderSupplierRow = (item: SupplierRfqInboxItemDto) => {
    const decision = supplierDecisionStatus(item)
    const isActive = selectedSupplierInboxId === item.inboxId
    return (
      <li key={item.inboxId} className="echat-inbox__item">
        <button
          type="button"
          className={isActive ? 'echat-inbox__row echat-inbox__row--active' : 'echat-inbox__row'}
          onClick={() => onSelectSupplierItem(item)}
        >
          <span className="echat-inbox__body">
            <span className="echat-inbox__name">{item.rfqTitle}</span>
            <span className="echat-inbox__preview market-inbox-row__meta">
              <StatusPill status={decision} />
              <span className="echat-muted">
                {restaurantDisplayName(item)} · {formatDate(item.createdAt)}
              </span>
            </span>
          </span>
        </button>
      </li>
    )
  }

  const renderRestaurantRow = (rfq: RfqDto) => {
    const decision = restaurantSupplierDecision(rfq)
    const statusLabel =
      decision === 'awaiting_customer_confirmation' ? formatStatus(decision) : formatStatus(rfq.status)
    const badgeStatus =
      decision === 'awaiting_customer_confirmation' || decision === 'substitution_proposed'
        ? decision
        : rfq.status
    const isActive = selectedRestaurantRfqId === rfq.id

    return (
      <li key={rfq.id} className="echat-inbox__item">
        <button
          type="button"
          className={isActive ? 'echat-inbox__row echat-inbox__row--active' : 'echat-inbox__row'}
          onClick={() => onSelectRestaurantRfq(rfq)}
        >
          <span className="echat-inbox__body">
            <span className="echat-inbox__name">{rfqDisplayLabel(rfq)}</span>
            <span className="echat-inbox__preview market-inbox-row__meta">
              <StatusPill status={badgeStatus} label={statusLabel} />
              <span className="echat-muted">{formatDate(rfq.createdAt)}</span>
            </span>
          </span>
        </button>
      </li>
    )
  }

  const renderArchiveList = () => {
    if (archivedCount === 0) {
      return <p className="echat-archive__empty echat-muted">В архиве пока нет заявок</p>
    }

    return (
      <div className="echat-inbox echat-inbox--archive">
        <ul className="echat-inbox__list">
          {isSupplier
            ? archivedSupplierOrders.map(renderSupplierRow)
            : archivedRestaurantOrders.map(renderRestaurantRow)}
        </ul>
      </div>
    )
  }

  return (
    <section aria-label="Список заявок" className="echat-orders echat-orders--list">
      {actionFilter ? (
        <div className="market-inline-actions echat-orders__filter-banner" role="status">
          <span className="echat-badge echat-badge--warning">Нужен ответ</span>
          <span className="echat-muted">Показаны заявки, где требуется ваше решение</span>
          <button type="button" className="echat-btn echat-btn--ghost echat-btn--sm" onClick={onClearActionFilter}>
            Показать все
          </button>
        </div>
      ) : null}
      <div className="echat-orders__scroll">
        {error ? (
          <p className="echat-error" role="alert">
            {error}
          </p>
        ) : null}

        {archiveExpanded ? (
          renderArchiveList()
        ) : totalCount === 0 ? (
          <MarketEmptyState
            title={
              actionFilter
                ? 'Заявок, требующих ответа, нет'
                : isSupplier
                  ? 'Входящих заявок нет'
                  : 'Заявок пока нет'
            }
            description={
              actionFilter
                ? 'Когда поставщик предложит замену или отметит выполнение, заявка появится здесь.'
                : isSupplier
                  ? 'Новые заявки от ресторанов появятся здесь.'
                  : uiCopy.ordersEmptyDescription
            }
            actions={
              actionFilter
                ? [{ label: 'Показать все', onClick: onClearActionFilter, variant: 'ghost' }]
                : isSupplier
                  ? undefined
                  : isStartPlan
                    ? [
                        {
                          label: startPlanUiCopy.ordersEmptyPrimaryAction.label,
                          to: startPlanUiCopy.ordersEmptyPrimaryAction.to,
                        },
                        {
                          label: startPlanUiCopy.ordersEmptySecondaryAction.label,
                          to: startPlanUiCopy.ordersEmptySecondaryAction.to,
                          variant: startPlanUiCopy.ordersEmptySecondaryAction.variant,
                        },
                      ]
                    : [
                        { label: 'Каталог', to: '/catalog' },
                        { label: 'Из корзины', to: '/orders/create', variant: 'ghost' },
                      ]
            }
          />
        ) : activeCount > 0 ? (
          <div className="echat-inbox">
            <ul className="echat-inbox__list">
              {isSupplier
                ? activeSupplierOrders.map(renderSupplierRow)
                : activeRestaurantOrders.map(renderRestaurantRow)}
            </ul>
          </div>
        ) : (
          <p className="echat-muted echat-orders__empty-active">Активных заявок нет.</p>
        )}
      </div>

      <div className="echat-orders__footer">
        {actionFilter ? null : (
          <section className="echat-archive" aria-label="Архив заявок">
            <button
              type="button"
              className="echat-archive__toggle"
              aria-expanded={archiveExpanded}
              onClick={onToggleArchive}
            >
              <span>{archiveExpanded ? 'Скрыть архив' : 'Архив'}</span>
              <span className="echat-archive__count">{archivedCount}</span>
              <span className="echat-archive__chevron" aria-hidden="true">
                {archiveExpanded ? '▾' : '▸'}
              </span>
            </button>
          </section>
        )}
      </div>
    </section>
  )
}
